import type { BookMetadata } from '../components/reader/types';

export interface CitationContext {
  metadata: BookMetadata;
  locationLabel?: string;
}

/**
 * Short inline citation appended after copied text,
 * e.g. "[Leo Tolstoy. Anna Karenina, Page 4]".
 */
export function formatInlineCitation({ metadata, locationLabel }: CitationContext): string {
  const parts: string[] = [];
  if (metadata.author) parts.push(metadata.author);
  if (metadata.title) parts.push(metadata.title);
  const head = parts.join('. ');
  const tail = locationLabel ? `, ${locationLabel}` : '';
  return `[${head}${tail}]`;
}

/**
 * Copy payload: selected text followed by citation.
 */
export function formatTextWithReference(
  selectedText: string,
  context: CitationContext
): string {
  return `${selectedText.trim()}\n\n${formatInlineCitation(context)}`;
}

function locationToPageRange(locationLabel?: string): string | null {
  if (!locationLabel) return null;
  const match = locationLabel.match(/\d+/);
  return match ? match[0] : null;
}

/**
 * RIS reference format (Refworks, Zotero, Mendeley, etc.)
 * Spec: https://en.wikipedia.org/wiki/RIS_(file_format)
 */
export function buildRisReference({ metadata, locationLabel }: CitationContext): string {
  const page = locationToPageRange(locationLabel);
  const lines: string[] = ['TY  - BOOK'];
  if (metadata.author) lines.push(`AU  - ${metadata.author}`);
  if (metadata.publisher) lines.push(`PB  - ${metadata.publisher}`);
  if (metadata.isbn) lines.push(`SN  - ${metadata.isbn}`);
  if (metadata.title) lines.push(`TI  - ${metadata.title}`);
  if (page) {
    lines.push(`SP  - ${page}`);
    lines.push(`EP  - ${page}`);
  }
  lines.push('ER  - ');
  return lines.join('\n') + '\n';
}

/**
 * EndNote (.enw) reference format.
 */
export function buildEndnoteReference({ metadata, locationLabel }: CitationContext): string {
  const page = locationToPageRange(locationLabel);
  const lines: string[] = ['%0 Electronic Book'];
  if (metadata.author) lines.push(`%A ${metadata.author}`);
  if (metadata.publisher) lines.push(`%I ${metadata.publisher}`);
  if (metadata.isbn) lines.push(`%@ ${metadata.isbn}`);
  if (metadata.title) lines.push(`%T ${metadata.title}`);
  if (page) lines.push(`%P ${page}-${page}`);
  return lines.join('\n') + '\n\n';
}

export type ReferenceFormat = 'ris' | 'endnote';

export function buildReference(format: ReferenceFormat, context: CitationContext): string {
  return format === 'ris' ? buildRisReference(context) : buildEndnoteReference(context);
}

export function referenceFilename(format: ReferenceFormat, metadata: BookMetadata, locationLabel?: string): string {
  const title = (metadata.title || 'reference')
    .replace(/[^a-z0-9\-_ ]/gi, '')
    .trim()
    .replace(/\s+/g, '_');
  const page = locationToPageRange(locationLabel);
  const suffix = page ? `_page_${page}` : '';
  const ext = format === 'ris' ? 'ris' : 'enw';
  return `${title}${suffix}.${ext}`;
}
